"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Images, Icons } from "@assets/assets";
import Image from "next/image";

interface Author {
  name: string;
  title: string;
}

interface TestimonialItem {
  name: string;
  subtitle: string;
  testimonial: string;
  video?: string;
  author: Author;
}

interface Props {
  baseTestimonials: TestimonialItem[];
}

export default function TestimonialClient({ baseTestimonials }: Props) {
  // ✅ Base data
  const base = useMemo(() => baseTestimonials ?? [], [baseTestimonials]);
  const baseLength = base.length;

  // ✅ Always 3x (NO mutation later)
  const testimonials = useMemo(() => {
    return [...base, ...base, ...base];
  }, [base]);

  // ✅ Start from middle block
  const [activeIndex, setActiveIndex] = useState(baseLength);

  const [mounted, setMounted] = useState(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLDivElement | null>>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const initialMountRef = useRef(true);

  // ✅ Mount
  useEffect(() => {
    setMounted(true);
  }, []);

  // ✅ Center active item
  useEffect(() => {
    const wrapperEl = wrapperRef.current;
    const tabEl = tabRefs.current[activeIndex];
    if (!wrapperEl || !tabEl) return;

    requestAnimationFrame(() => {
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const tabRect = tabEl.getBoundingClientRect();

      const centeredLeft =
        wrapperEl.scrollLeft +
        (tabRect.left - wrapperRect.left) -
        (wrapperRect.width - tabRect.width) / 2;

      if (initialMountRef.current) {
        wrapperEl.scrollLeft = centeredLeft;
        initialMountRef.current = false;
      } else {
        wrapperEl.scrollTo({ left: centeredLeft, behavior: "smooth" });
      }
    });
  }, [activeIndex]);

  // ✅ Infinite loop WITHOUT DOM mutation
  useEffect(() => {
    if (activeIndex < baseLength) {
      setActiveIndex((prev) => prev + baseLength);
    } else if (activeIndex >= baseLength * 2) {
      setActiveIndex((prev) => prev - baseLength);
    }
  }, [activeIndex, baseLength]);

  // NOTE: Mobile (<1366px) visibility is now handled purely with CSS below
  // (`hidden min-[1366px]:block`) instead of a JS matchMedia + `return null`.
  // The old approach SSR'd the full desktop layout, then unmounted it on the
  // client for mobile viewports — collapsing the section and causing a large
  // CLS. A CSS media-query gate is applied on the first paint identically on
  // server and client, so there is no hydration shift. Content is unchanged.
  if (!base.length) return null;

  // ✅ Hover video handlers
  const handleMouseEnter = async (index: number) => {
    if (index !== activeIndex) return;
    if (!testimonials[index]?.video) return;

    const video = videoRef.current;
    if (!video) return;

    try {
      video.muted = false;
      await video.play();
    } catch {
      video.muted = true;
      try {
        await video.play();
      } catch {}
    }
  };

  const handleMouseLeave = (index: number) => {
    if (index !== activeIndex) return;

    const video = videoRef.current;
    if (!video) return;

    video.pause();
    video.currentTime = 0;
  };

  // ✅ Navigation
  const handleNext = () => setActiveIndex((prev) => prev + 1);
  const handlePrev = () => setActiveIndex((prev) => prev - 1);
  const handleTabClick = (index: number) => setActiveIndex(index);

  // ✅ Display index (1-based)
  const displayIndex =
    ((activeIndex - baseLength + baseLength) % baseLength) + 1;

  return (
    <section className="hidden min-[1366px]:block bg-[#fff] w-full relative testimonial-section">
      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] py-[50px] pb-[30px] md:py-[60px] lg:py-[150px]">
        {/* Header */}
        <div className="testimonial-header flex items-center gap-[15px]">
          <div className="testimonial-header-left leading-[110%]">
            <p className="self-stretch text-[#636363] text-base font-medium uppercase tracking-tight pb-4">
              testimonials
            </p>
            <h2 className="self-stretch text-[length:var(--section-title)] font-medium clash-font min-w-[16ch]">
              <span className="text-[#00040b] capitalize leading-[110%]">
                Words of Appreciation from
              </span>
              <span className="text-[#1D4ED8] capitalize leading-[110%]">
                &nbsp;Our Clients
              </span>
            </h2>
          </div>
        </div>

        {/* Navigation */}
        <div className="testimonial-slider-navigation flex justify-end items-center w-full pt-[20px] lg:pt-[120px]">
          <div className="flex gap-2 sm:gap-[15px] items-center">
            <Image
              onClick={handlePrev}
              src={Icons?.slideLeft as any}
              alt="previous"
              width={40}
              height={40}
              className="cursor-pointer w-[32px] h-[32px] sm:w-[40px] sm:h-[40px]"
              style={{ width: "auto", height: "auto" }}
              sizes="(max-width: 1024px) 100vw, 50vw"
              loading="lazy"
            />
            <span className="flex items-center font-bold text-[14px] sm:text-lg">
              <span className="text-[#1D4ED8]">
                {String(displayIndex).padStart(2, "0")}
              </span>
              <span className="mx-1 text-[#1D4ED8]">/</span>
              <span>{String(base.length).padStart(2, "0")}</span>
            </span>
            <Image
              onClick={handleNext}
              src={Icons?.slideRight as any}
              alt="next"
              width={40}
              height={40}
              className="cursor-pointer w-[32px] h-[32px] sm:w-[40px] sm:h-[40px]"
              style={{ width: "auto", height: "auto" }}
              sizes="(max-width: 1024px) 100vw, 50vw"
              loading="lazy"
            />
          </div>
        </div>

        {/* Slider */}
        <div
          className="testimonial-slider-tab pt-[40px] overflow-hidden"
          ref={wrapperRef}
        >
          <div className="slider-scroller flex gap-[10px] lg:gap-[30px]">
            {testimonials?.map((tab, index) => {
              const isActive = index === activeIndex;
              const hasVideo = !!tab.video;

              return (
                <div
                  key={`${index}-${tab.name}`}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  onClick={() => handleTabClick(index)}
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={() => handleMouseLeave(index)}
                  className={`slider-tab-content group flex flex-col min-w-[100%] lg:min-w-[calc(var(--testimonial-box)_-_12px)] flex-1 transition-all duration-300 cursor-pointer ${
                    isActive ? "opacity-100 scale-100" : "opacity-20 scale-95"
                  }`}
                >
                  {/* Top Info Bar */}
                  <div className="flex gap-[30px] w-full justify-between pb-3 border-b border-[#ECF0F6] items-center">
                    <div className="flex flex-col gap-0">
                      <h3 className="font-[700] text-[length:var(--banner-para)]">
                        {tab.name}
                      </h3>
                      <p>{tab.subtitle}</p>
                    </div>
                    <Image
                      src={Images?.rating as any}
                      alt="rating"
                      width={129}
                      height={40}
                      className="w-[129px] h-[40px]"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      loading="lazy"
                    />
                  </div>

                  {/* Content Area */}
                  <div className="testimonial-slider-content pt-[40px] flex flex-col justify-between h-full relative overflow-hidden">
                    {/* ✅ Video overlay — only rendered for active card with video */}
                    {hasVideo && mounted && isActive ? (
                      <div className="absolute inset-0 z-20 transition-opacity duration-500 bg-white opacity-0 group-hover:opacity-100">
                        {/* 
                          ✅ Single <video> via ref.
                          Key stays stable — no remount on slide change.
                          Only exists in DOM for the ONE active card.  
                        */}
                        <video
                          ref={videoRef}
                          src={`${tab?.video}`}
                          playsInline
                          preload="none"
                          loop
                          muted
                          className="w-full h-full object-cover pointer-events-none"
                        />
                      </div>
                    ) : null}

                    <div className="flex flex-col items-start gap-[30px]">
                      <Image
                        src={Icons?.quote as any}
                        alt="quote"
                        width={50}
                        height={50}
                        className="size-[50px]"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        loading="lazy"
                      />
                      <p className="self-stretch text-[#00040b] text-[length:var(--testimonial-para)] italic font-medium leading-[130%]">
                        {tab.testimonial}
                      </p>
                      <div className="flex gap-[20px] items-center">
                        <div className="flex flex-col">
                          <p className="text-[length:var(--banner-para)] text-[#00040b] text-lg font-medium clash-font capitalize">
                            {tab.author.name}
                          </p>
                          <p className="text-[#636363] text-[length:var(--ai-button-text)] font-medium uppercase tracking-tight">
                            {tab.author.title}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
